"use client"

import { cn } from "@/lib/utils"

/**
 * The strip of statuses that sits under a list page's title.
 *
 * Jobs and Candidates each had their own copy of it, and the copies had already
 * drifted — one carried a count beside each name and the other did not, and
 * both hid the whole strip below `@2xl`. Hiding it is the part that mattered:
 * the strip is the only way to reach anything that is not in the first tab, so
 * on a phone three quarters of each list was unreachable. It scrolls sideways
 * now instead of going away.
 *
 * Counts are optional because only one of the two pages can produce them
 * cheaply; a tab without one simply shows its name.
 */
export function RecordTabs<T extends string>({
  tabs,
  value,
  counts,
  onChange,
  className,
}: {
  tabs: readonly T[]
  value: T
  /** Optional — how many records each tab holds. */
  counts?: Record<T, number>
  onChange: (next: T) => void
  className?: string
}) {
  return (
    /*
      The negative margin lets the strip bleed to the page's edges while its
      padding keeps the first and last tab off them, so a scrolled strip runs
      under the edge rather than stopping short of it. `scrollbar-width: none`
      because a scrollbar under four words is louder than the words.
    */
    <div
      className={cn(
        "-mx-4 mt-4 flex gap-5 overflow-x-auto border-b px-4 [scrollbar-width:none] @2xl:mx-0 @2xl:px-0",
        className,
      )}
    >
      {tabs.map((name) => (
        <button
          key={name}
          type="button"
          onClick={() => onChange(name)}
          aria-current={value === name ? "page" : undefined}
          className={cn(
            "-mb-px inline-flex flex-none items-baseline gap-1.5 border-b-2 px-0.5 py-2 text-[12.5px] transition-colors",
            value === name
              ? "border-primary font-medium text-foreground"
              : "border-transparent text-muted-foreground hover:text-foreground",
          )}
        >
          {name}
          {counts && (
            <span className="font-mono text-[10.5px] text-muted-foreground">
              {counts[name]}
            </span>
          )}
        </button>
      ))}
    </div>
  )
}
